import Dexie from 'dexie';
import { db } from '$db/local/db';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, estimatePayloadBytes } from '../repositories/common';
import type { OutboxOperation } from '$db/local/db';

/**
 * Bridge automático (M4.3 fix).
 * Escucha los cambios de Dexie y encola automáticamente en la outbox.
 * Ningún repo necesita llamar a enqueueOperation manualmente: los hooks
 * capturan todas las mutaciones, incluidas las futuras.
 *
 * Supresión: durante el merge (pull/apply remoto) y las migraciones,
 * las escrituras deben NO encolar. Se controla con un contador de
 * profundidad para soportar anidamiento.
 */

const TABLE_TO_ENTITY: Record<string, string> = {
  nodes: 'node',
  nodeTemplates: 'nodeTemplate',
  macroSessions: 'macroSession',
  problemAttempts: 'problemAttempt',
  recallItems: 'recallItem',
  reviewEvents: 'reviewEvent',
  memoryWeights: 'memoryWeights',
  conjectures: 'conjecture',
  sleepRecords: 'sleepRecord',
  studyGoals: 'studyGoal',
  preferences: 'preferences'
};

let suppressionDepth = 0;

export function isSuppressed(): boolean {
  return suppressionDepth > 0;
}

export async function withSuppression<T>(fn: () => Promise<T>): Promise<T> {
  suppressionDepth += 1;
  try {
    return await fn();
  } finally {
    suppressionDepth -= 1;
  }
}

interface DexieChange {
  type: 'created' | 'updated' | 'deleted';
  table: string;
  key: unknown;
  obj: Record<string, unknown>;
}

function buildOp(change: DexieChange): OutboxOperation | null {
  const entityType = TABLE_TO_ENTITY[change.table];
  if (!entityType) return null;

  const id = change.obj.id;
  if (typeof id !== 'string') return null;

  const now = nowIso();
  const isDelete = change.type === 'deleted';

  const payload: unknown = isDelete ? null : change.obj;

  return {
    id: newId(),
    userId: getActiveUserId(),
    entityType,
    entityId: id,
    op: isDelete ? 'delete' : 'upsert',
    payload,
    createdAt: now,
    updatedAt: now,
    idempotencyKey: newId(),
    attempts: 0,
    status: 'pending',
    bytes: estimatePayloadBytes(payload),
    schemaVersion: 1
  };
}

let installed = false;

function enqueueOne(op: OutboxOperation): void {
  const tx = Dexie.currentTransaction;
  if (!tx) {
    void db.outbox.add(op).catch((err) => {
      console.error('[StudyQuantifier] bridge enqueue failed:', err);
    });
    return;
  }
  tx.on('complete', () => {
    void db.outbox.add(op).catch((err) => {
      console.error('[StudyQuantifier] bridge enqueue failed:', err);
    });
  });
}

export function installBridge(): void {
  if (installed) return;
  installed = true;

  for (const tableName of Object.keys(TABLE_TO_ENTITY)) {
    const table = db.table(tableName);
    if (!table) continue;

    table.hook('creating', function (primKey, obj, _transaction) {
      if (isSuppressed()) return;
      const op = buildOp({
        type: 'created',
        table: tableName,
        key: primKey,
        obj: obj as Record<string, unknown>
      });
      if (op) enqueueOne(op);
    });

    table.hook('updating', function (mods, primKey, obj, _transaction) {
      if (isSuppressed()) return;
      const merged = {
        ...(obj as Record<string, unknown>),
        ...(mods as Record<string, unknown>)
      };
      const op = buildOp({
        type: 'updated',
        table: tableName,
        key: primKey,
        obj: merged
      });
      if (op) enqueueOne(op);
    });

    table.hook('deleting', function (primKey, obj, _transaction) {
      if (isSuppressed()) return;
      const op = buildOp({
        type: 'deleted',
        table: tableName,
        key: primKey,
        obj: obj as Record<string, unknown>
      });
      if (op) enqueueOne(op);
    });
  }

  if (import.meta.env.DEV) {
    console.info('[StudyQuantifier] Dexie sync bridge installed.');
  }
}